import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, MoveUpRight } from "lucide-react";
import "./LandingPage.css";
import FrequentlyAsked from "../components/frequentlyAsked/FrequentlyAsked";
import CTASection from "../components/ctaSection/CTASection";
import Footer from "../components/footer/Footer";
import HostEvents from "../components/hostEvents/HostEvents";
import SideNavBar from "../components/sideNavBar/SideNavBar";
import Slider from "react-slick";
import ExploreSection from "../components/exploreSection/ExploreSection";
import { useAuth } from "../context/auth";

const heroImage1 = "/images/bimage1.png";
const heroImage2 = "/images/bimage2.png";
const heroImage3 = "/images/bimage3.jpg";

const decorativeImage = "/images/00180.png";
const logoImage = "/images/logo_white.png";
const logoImage2 = "/images/logoOnBlack.png";
const featurePhone1 = "/images/event_management_image.png";
const featurePhone2 = "/images/mobile_app.png";
const eventImage1 = "/images/built1.png";
const eventImage2 = "/images/bult2.png";
const giftBoxImage = "/images/gift-box.jpg";
const refund = "/images/refund_management.png";
const rsvps = "/images/rsvps.png";
const securedPayment = "/images/secured_payment.png";
const notifications = "/images/notification_system.png";
const frequentlyAsked = "/images/frequently_asked.png";
const phone = "/images/phone1.png";
const phone2 = "/images/phone2.png";
const location = "/images/location.png";

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleNavClick = (path) => {
    navigate(path);
    closeSidebar();
  };

  const handleLogin = () => {
    navigate("/onboarding", { state: { mode: "login" } });
    closeSidebar();
  };

  const handleGetStarted = () => {
    navigate("/onboarding", { state: { mode: "signup" } });
    closeSidebar();
  };

  const getDashboardPath = () => {
    if (!user?.activeRole) return "/";
    return `/home/${user.activeRole}`;
  };

  const handleOpenGooglePlay = () => {
    window.open(
      "https://play.google.com/store/apps/details?id=com.nimblebash.nimblebyte",
      "_blank",
    );
  };

  const handleOpenAppStore = () => {
    window.open("https://apps.apple.com/app/aso-ebi/id1604032509", "_blank");
  };

  const handleOpenWebSignup = () => {
    navigate("/onboarding");
  };

  const customSteps = [
    {
      title: "Create & Customize Your Event",
      description:
        "Create your event by adding your event title, date, location, and Aso Ebi price. You're ready to go live..",
      image: "/images/create_event.png",
      badge: "Design Your Event",
    },
    {
      title: "Invite Your Guests",
      description:
        "Send digital invitations via WhatsApp, SMS, or email. Guests can RSVP with one click..",
      image: "/images/share_link.png",
      badge: "Manage Guest List",
    },
    {
      title: "Collect Contributions & Gifts",
      description:
        "Secure payments for Aso Ebi and cash gifts through Paystack. Automatic confirmations included..",
      image: "/images/ticket_image.png",
      badge: "Track Everything",
    },
  ];
  const settings = {
    arrows: false,
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    slidesToScroll: 1,
    adaptiveHeight: false,
  };
  return (
    <div className="landing-page-container">
      <header className="landing-page-header">
        <div className="landing-page-header-content">
          <div className="landing-page-logo">
            <img
              src={logoImage2}
              alt="Logo"
              className="landing-page-hero-logo"
              onClick={() => navigate("/")}
            />
          </div>

          <nav className="landing-page-nav-desktop">
            <a href="/" className="landing-page-nav-link">
              Home
            </a>
            <a href="/explore" className="landing-page-nav-link">
              Explore
            </a>
            <a href="/about-us" className="landing-page-nav-link">
              About Us
            </a>
            <a href="/get-in-touch" className="landing-page-nav-link">
              Get help
            </a>
          </nav>

          <div className="landing-page-header-buttons">
            {!isAuthenticated ? (
              <>
                <button
                  className="landing-page-login-button-desktop"
                  onClick={handleLogin}
                >
                  Login
                </button>
                <button
                  className="landing-page-create-account-button-desktop"
                  onClick={handleGetStarted}
                >
                  Get Started
                </button>
              </>
            ) : (
              <button
                className="landing-page-create-account-button-desktop"
                onClick={() => navigate(getDashboardPath())}
              >
                Dashboard
              </button>
            )}
          </div>

          <button className="landing-page-hamburger" onClick={toggleSidebar}>
            <Menu size={28} /> Menu
          </button>
        </div>
      </header>

      <SideNavBar
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        activeNav="home"
        onNavClick={handleNavClick}
        onLoginClick={handleLogin}
        onCreateAccountClick={handleGetStarted}
        isAuthenticated={isAuthenticated}
        user={user}
      />

      <section className="landing-page-hero" id="home">
        <div className="landing-page-hero-card">
          <img
            src={decorativeImage}
            alt="Decorative element"
            className="landing-page-hero-decorative"
          />

          <div className="landing-page-hero-content">
            <div className="landing-page-hero-text">
              <h5 className="landing-page-hero-title">Event Management</h5>
              <h1 className="landing-page-hero-title">
                <span className="landing-page-hero-span">Made </span> Effortless
              </h1>
              <p className="landing-page-hero-subtitle">
                Streamline your event from start to finish. Our event management
                app brings together invitations, guest tracking, and check-in
                tools to help you deliver flawless experiences every time.
              </p>
            </div>
            <div className="landing-page-hero-image-wrapper">
              <Slider {...settings}>
                <div className="landing-page-hero-image-landing">
                  {" "}
                  <img src={heroImage1} alt="Event celebration" />
                </div>
                <div className="landing-page-hero-image-landing">
                  {" "}
                  <img src={heroImage2} alt="Event celebration" />
                </div>
                <div className="landing-page-hero-image-landing">
                  <img src={heroImage3} alt="Event celebration" />
                </div>
              </Slider>
            </div>
          </div>
        </div>
        <div className="landing-page-hero-buttons-wrapper">
          <div className="landing-page-hero-buttons">
            <button
              onClick={handleOpenGooglePlay}
              className="landing-page-hero-button landing-page-hero-button-google"
            >
              <span className="landing-page-download-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="34"
                  height="28"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="#34A853"
                    d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5Z"
                  />
                  <path
                    fill="#4285F4"
                    d="M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M6.05,21.34L14.54,12.85L16.81,15.12L6.05,21.34Z"
                  />
                  <path
                    fill="#EA4335"
                    d="M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.92 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81Z"
                  />
                </svg>
              </span>
              Google Play
            </button>
            <button
              onClick={handleOpenAppStore}
              className="landing-page-hero-button landing-page-hero-button-apple"
            >
              <span className="landing-page-download-icon">
                <svg viewBox="0 0 24 24" width="32" height="32">
                  <path
                    fill="currentColor"
                    d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"
                  />
                </svg>
              </span>
              Apple Store
            </button>
            <button
              onClick={handleOpenWebSignup}
              className="landing-page-hero-button landing-page-hero-button-web"
            >
              Sign up on web
            </button>
          </div>
        </div>
      </section>

      <ExploreSection />

      <section className="landing-page-features">
        <div className="landing-page-features-intro">
          <h2 className="landing-page-section-title">
            Explore Our Essential and Powerful Features to Simplify and give you
            a memorable Event.
          </h2>
        </div>

        <div className="landing-page-feature-block">
          <div className="landing-page-feature-content">
            <img
              src={featurePhone1}
              alt="Event Management Interface"
              className="landing-page-feature-main-image"
            />
          </div>
          <div className="landing-page-feature-text">
            <h3 className="landing-page-feature-label">Event Management</h3>
            <h2 className="landing-page-feature-title-large">
              Create, edit or Manage events. Monitor guests lists and
              contribution statuses easily.
            </h2>
            <p className="landing-page-feature-description">
              With NimbleBash, you cannot only create events and share invite
              but can also receive cash gift from guest, get payment for event
              attire among all other things on the platform.
            </p>
          </div>
        </div>

        <div className="landing-page-feature-cards-row">
          <div className="landing-page-feature-small-card-one">
            <div className="landing-page-feature-small-card">
              <div className="landing-page-feature-card-icon">
                <div className="landing-page-feature-icon-placeholder">
                  <img
                    src={refund}
                    alt="Logo"
                    className="landing-page-hero-icon"
                  />
                </div>
              </div>
              <h4 className="landing-page-feature-card-title">
                Event Management
              </h4>
              <p className="landing-page-feature-card-description">
                Easily create, organize, and oversee events from start to
                finish. Track invites, manage guest lists, and coordinate all
                event details in one convenient place.
              </p>
            </div>

            <div className="landing-page-feature-small-card">
              <div className="landing-page-feature-card-icon">
                <div className="landing-page-feature-icon-placeholder">
                  <img
                    src={securedPayment}
                    alt="Logo"
                    className="landing-page-hero-icon"
                  />
                </div>
              </div>
              <h4 className="landing-page-feature-card-title">
                Secured Payment Integration
              </h4>
              <p className="landing-page-feature-card-description">
                Enjoy fast and reliable in-app payments. From ticket purchases
                to cash gifts, every transaction is encrypted and protected for
                a safe user experience.
              </p>
            </div>
          </div>

          <div className="landing-page-feature-small-card-center">
            <img
              src={featurePhone2}
              alt="Mobile App"
              className="landing-page-feature-card-phone"
            />
          </div>

          <div className="landing-page-feature-small-card-one">
            <div className="landing-page-feature-small-card">
              <div className="landing-page-feature-card-icon">
                <div className="landing-page-feature-icon-placeholder">
                  <img
                    src={rsvps}
                    alt="Logo"
                    className="landing-page-hero-icon"
                  />
                </div>
              </div>
              <h4 className="landing-page-feature-card-title">
                Smart Event Invitation
              </h4>
              {/* <p className="landing-page-feature-card-description">
                Send personalized, digital invitations with unique invite codes
                and invite links. Guests can accept invite instantly, making
                event attendance smooth and stress-free, and hosts get real-time
                updates on the event dashboard as each guest accepts their
                invite, making attendance tracking effortless and accurate.
              </p> */}
              <p className="landing-page-feature-card-description">
                Send personalized digital invitations with unique codes and
                links. Guests accept instantly. You get real-time updates on
                your dashboard as each guest responds, so attendance stays
                accurate and easy to track.
              </p>
            </div>

            <div className="landing-page-feature-small-card">
              <div className="landing-page-feature-card-icon">
                <div className="landing-page-feature-icon-placeholder">
                  <img
                    src={notifications}
                    alt="Logo"
                    className="landing-page-hero-icon"
                  />
                </div>
              </div>
              <h4 className="landing-page-feature-card-title">
                Notifications System
              </h4>
              <p className="landing-page-feature-card-description">
                Keep everyone in the loop with real-time updates. From event
                reminders to in-app alerts and attendance confirmations.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-page-everyone">
        <div className="landing-page-event-cards">
          <div className="landing-page-event-card-one">
            <h2 className="landing-page-section-title-center">
              Built for everyone
            </h2>
            <p className="landing-page-section-subtitle">
              NimbleBash is s seamless platform that simplifies event planning,
              guest management, and secure payments.
            </p>

            <div className="landing-page-event-card">
              <img
                src={eventImage1}
                alt="Host Event"
                className="landing-page-event-card-image"
              />
              <div className="landing-page-event-card-overlay">
                <div className="landing-page-event-card-content">
                  <h3 className="landing-page-event-card-title">Host Event</h3>
                  <p className="landing-page-event-card-description">
                    Easily manage your event from start to finish, with full
                    control over invitations and aso-ebi sales. All from the
                    comfort of your home.
                  </p>
                </div>
                <button className="landing-page-event-card-arrow">
                  <MoveUpRight />
                </button>
              </div>
            </div>
          </div>

          <div className="landing-page-event-card-two">
            <img
              src={eventImage2}
              alt="Be a Guest & Attend Event"
              className="landing-page-event-card-image"
            />
            <div className="landing-page-event-card-overlay">
              <div className="landing-page-event-card-content">
                <h3 className="landing-page-event-card-title">
                  Be a Guest & Attend Event
                </h3>
                <p className="landing-page-event-card-description">
                  Get invite link or code and manage your invite, pay for event
                  attire, tickets, or contribute cash gift to event host - all
                  in one app.
                </p>
              </div>
              <button className="landing-page-event-card-arrow">
                <MoveUpRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      <HostEvents />

      <FrequentlyAsked />

      <CTASection
        title="Join others to"
        highlight1="Xperience Luxury Event"
        highlight2="Management"
        description="Join thousands of users in elevating your event experience! Plan, manage, and attend events with ease, while sending cash gifts to your loved ones. Download our app on Google Play or Apple Store, or sign up on our website to enjoy our web version."
        image={phone2}
        onGooglePlayClick={() =>
          window.open(
            "https://play.google.com/store/apps/details?id=com.nimblebash.nimblebyte",
            "_blank",
          )
        }
        onAppleStoreClick={() =>
          window.open("https://apps.apple.com/ng/app/nimblebash/id6752389351")
        }
        onWebSignupClick={() => navigate("/onboarding")}
      />

      <Footer />
    </div>
  );
};

export default LandingPage;
